import HttpClient from "./httpClient";
import {
  buildConsolidatedAuditQueryParams,
  buildUsageQueryParams,
  type ConsolidatedAuditFilterParams,
} from "./usageApiUtils";

export interface UsageFilterParams {
  startDate?: string | null;
  endDate?: string | null;
  userEmail?: string | null;
  limit?: number;
  includeRootUsers?: boolean;
  /** ROOT only: scope usage to this Horizon MSP id (server-validated). */
  mspId?: number | null;
}

export interface UsageSummary {
  actions: number;
  ticket_actions: number;
  assistant_actions: number;
  /** Distinct PSA tickets with Theo tool runs in the period (not capped by usage_by_ticket limit). */
  tickets_touched?: number;
}

export interface UsageByUserTicketsRow {
  user_email?: string | null;
  ticket_count: number;
  actions: number;
}

export interface UsageByUserAssistantRow {
  user_email?: string | null;
  actions: number;
}

export interface UsageByTicketRow {
  ticket_id: string;
  actions: number;
}

export interface UsageByHaloCategoryTicketActionRun {
  tool_name: string;
  estimated_minutes_saved: number;
  start_timestamp?: string | null;
  audit_id?: number | null;
}

export interface UsageByHaloCategoryTicketRow {
  ticket_id: string;
  actions: number;
  estimated_minutes_saved?: number;
  ticket_title?: string | null;
  status_name?: string | null;
  client_name?: string | null;
  /** Individual tool runs in execution order (start_timestamp / audit_id). */
  action_runs?: UsageByHaloCategoryTicketActionRun[];
}

export interface UsageByHaloCategoryRow {
  category_id: string;
  category_name: string;
  actions: number;
  ticket_count: number;
  /**
   * Tickets created or updated in the selected period in this category, plus any
   * Theo-touched tickets in the period (so Total is never below Theo tickets).
   * Null when the universe query was unavailable.
   */
  category_ticket_count?: number | null;
  /**
   * Theo tickets ÷ Total for this category (0–100, capped). Null when the
   * denominator is unknown or zero.
   */
  ticket_coverage_pct?: number | null;
  /** Estimated minutes saved from per-tool heuristics for actions in this Halo category. */
  estimated_minutes_saved?: number;
  /**
   * Always empty on the main Usage payload. Load Theo tickets via
   * `UsageApi.getHaloCategoryTickets({ mode: "theo" })` on drilldown click.
   */
  tickets?: UsageByHaloCategoryTicketRow[];
  /**
   * Always empty on the main Usage payload. Load Total tickets via
   * `UsageApi.getHaloCategoryTickets({ mode: "total" })` on drilldown click.
   */
  total_tickets?: UsageByHaloCategoryTicketRow[];
}

export interface UsageByToolRow {
  tool_name: string;
  actions: number;
  ticket_actions: number;
  assistant_actions: number;
}

export interface UsageDailyRow {
  date: string | null;
  actions: number;
}

export interface UsageResponse {
  summary: UsageSummary;
  filters: {
    startDate: string | null;
    endDate: string | null;
    userEmail: string | null;
    limit: number;
    includeRootUsers?: boolean;
  };
  usage_by_user_tickets: UsageByUserTicketsRow[];
  usage_by_user_assistant: UsageByUserAssistantRow[];
  usage_by_ticket: UsageByTicketRow[];
  daily_tickets: UsageDailyRow[];
  daily_assistant: UsageDailyRow[];
  usage_by_tool: UsageByToolRow[];
  available_users: string[];
  /** True when the scoped MSP has an enabled Halo PSA platform. */
  has_halo_psa?: boolean;
  /** Present only when `has_halo_psa` is true — ticket actions by Halo Category. */
  usage_by_halo_category?: UsageByHaloCategoryRow[];
}

interface ApiResponse<T> {
  status?: string;
  data?: T;
}

interface MspUserEmailsPayload {
  emails?: string[];
}

/** ROOT-only Usage MSP picker; id + name from Horizon `msp` table. */
export interface UsageMspOptionRow {
  id: number;
  name?: string | null;
}

interface UsageMspOptionsPayload {
  rows?: UsageMspOptionRow[];
}

/** Customers for Usage / Audit Logs filters; scoped to the same MSP as usage queries. */
export interface UsageCustomerOptionRow {
  id: number;
  name?: string | null;
}

interface UsageCustomerOptionsPayload {
  rows?: UsageCustomerOptionRow[];
}

export interface ConsolidatedAuditLogRow {
  id: string | null;
  ai_thread_id: string | null;
  tool_name: string;
  status: string | null;
  start_timestamp: string | null;
  end_timestamp: string | null;
  user_email: string | null;
  action_permission: string | null;
  ticket_id: string | null;
  customer_id: number | null;
  customer_name: string | null;
  context_type?: "ticket" | "assistant";
  thread_name?: string | null;
  thread_user_id?: string | null;
}

export interface ToolAuditLogEntryDetail {
  id: string | null;
  ai_thread_id: string | null;
  tool_name: string;
  reasoning: string | null;
  param_json: unknown;
  output_json: unknown;
  error_message: string | null;
  status: string | null;
  start_timestamp: string | null;
  end_timestamp: string | null;
  user_email: string | null;
  action_permission: string | null;
}

export interface ConsolidatedAuditLogsResponse {
  filters: {
    startDate: string | null;
    endDate: string | null;
    customerId: number | null;
    ticketId: string | null;
    limit: number;
    includeRootUsers?: boolean;
  };
  rows: ConsolidatedAuditLogRow[];
}

class UsageApi extends HttpClient {
  public getMspUserEmails = async (
    opts: { includeRootUsers?: boolean; mspId?: number | null } = {}
  ): Promise<string[]> => {
    const response: ApiResponse<MspUserEmailsPayload> | MspUserEmailsPayload = await this.get(
      "/usage/msp-user-emails",
      undefined,
      {
        includeRootUsers: opts.includeRootUsers ? "1" : undefined,
        mspId: opts.mspId != null ? String(opts.mspId) : undefined,
      }
    );

    let payload: MspUserEmailsPayload | undefined;
    if (response && typeof response === "object" && "data" in response && response.data) {
      payload = response.data as MspUserEmailsPayload;
    } else if (response && typeof response === "object") {
      payload = response as MspUserEmailsPayload;
    }

    const emails = payload?.emails;
    return Array.isArray(emails) ? emails.filter((e) => typeof e === "string" && e.trim() !== "") : [];
  };

  /** ROOT only: all MSPs for Usage filter dropdown (server returns 403 otherwise). */
  public getUsageMspOptionsForRoot = async (): Promise<UsageMspOptionRow[]> => {
    const response: ApiResponse<UsageMspOptionsPayload> | UsageMspOptionsPayload = await this.get(
      "/usage/msp-options"
    );

    let payload: UsageMspOptionsPayload | undefined;
    if (response && typeof response === "object" && "data" in response && response.data) {
      payload = response.data as UsageMspOptionsPayload;
    } else if (response && typeof response === "object") {
      payload = response as UsageMspOptionsPayload;
    }

    const rows = payload?.rows;
    return Array.isArray(rows) ? rows : [];
  };

  /** Customers for the selected MSP (same scope as usage); supports ROOT `mspId` query. */
  public getUsageCustomerOptions = async (opts: { mspId?: number | null } = {}): Promise<
    UsageCustomerOptionRow[]
  > => {
    const response: ApiResponse<UsageCustomerOptionsPayload> | UsageCustomerOptionsPayload =
      await this.get("/usage/customer-options", undefined, {
        mspId: opts.mspId != null ? String(opts.mspId) : undefined,
      });

    let payload: UsageCustomerOptionsPayload | undefined;
    if (response && typeof response === "object" && "data" in response && response.data) {
      payload = response.data as UsageCustomerOptionsPayload;
    } else if (response && typeof response === "object") {
      payload = response as UsageCustomerOptionsPayload;
    }

    const rows = payload?.rows;
    return Array.isArray(rows) ? rows : [];
  };

  public getConsolidatedAuditLogs = async (
    filters: ConsolidatedAuditFilterParams = {}
  ): Promise<ConsolidatedAuditLogsResponse> => {
    const response: ApiResponse<ConsolidatedAuditLogsResponse> | ConsolidatedAuditLogsResponse =
      await this.get("/usage/consolidated-audit-logs", undefined, buildConsolidatedAuditQueryParams(filters));

    if (response && typeof response === "object" && "data" in response && response.data) {
      return response.data as ConsolidatedAuditLogsResponse;
    }

    return response as ConsolidatedAuditLogsResponse;
  };

  public getToolAuditLogEntry = async (
    entryId: string,
    opts: { mspId?: number | null } = {}
  ): Promise<ToolAuditLogEntryDetail> => {
    const response: ApiResponse<ToolAuditLogEntryDetail> | ToolAuditLogEntryDetail = await this.get(
      `/usage/tool-audit-log-entry/${encodeURIComponent(entryId)}`,
      undefined,
      {
        mspId: opts.mspId != null ? String(opts.mspId) : undefined,
      }
    );

    if (response && typeof response === "object" && "data" in response && response.data) {
      return response.data as ToolAuditLogEntryDetail;
    }

    return response as ToolAuditLogEntryDetail;
  };

  public getUsage = async (filters: UsageFilterParams = {}): Promise<UsageResponse> => {
    const response: ApiResponse<UsageResponse> | UsageResponse = await this.get(
      "/usage/tool-audit-logs",
      undefined,
      buildUsageQueryParams(filters)
    );

    if (response && typeof response === "object" && "data" in response && response.data) {
      return response.data;
    }

    return response as UsageResponse;
  };

  /** On-demand Halo category ticket drilldown (Total or Theo). */
  public getHaloCategoryTickets = async (args: {
    categoryId: string;
    /** Display name from the Usage table row — keeps popup title aligned. */
    categoryName?: string | null;
    mode?: "total" | "theo";
    startDate?: string | null;
    endDate?: string | null;
    userEmail?: string | null;
    includeRootUsers?: boolean;
    mspId?: number | null;
  }): Promise<{ category_id: string; category_name: string; tickets: UsageByHaloCategoryTicketRow[] }> => {
    const response = await this.get("/usage/halo-category-tickets", undefined, {
      categoryId: args.categoryId,
      categoryName: args.categoryName || undefined,
      mode: args.mode || "total",
      startDate: args.startDate || undefined,
      endDate: args.endDate || undefined,
      userEmail: args.userEmail || undefined,
      includeRootUsers: args.includeRootUsers ? "1" : undefined,
      mspId: args.mspId != null ? String(args.mspId) : undefined,
    });

    if (response && typeof response === "object" && "data" in response && response.data) {
      return response.data as {
        category_id: string;
        category_name: string;
        tickets: UsageByHaloCategoryTicketRow[];
      };
    }

    return response as {
      category_id: string;
      category_name: string;
      tickets: UsageByHaloCategoryTicketRow[];
    };
  };
}

const usageApi = new UsageApi();

export default usageApi;
